import React from "react";
import PropTypes from "prop-types";
import Highlighter from "react-highlight-words";
import styled, { css } from "react-emotion";
import { ClickOutsideListener } from "components";
import { to } from "utilities";
import cuid from "cuid";
import Waypoint from "react-waypoint";
import { normalize, mergeDedupe } from "utilities";

class ListSearchBox extends React.Component {
  constructor() {
    super();
    this.initialState = {
      open: false,
      fetching: false,
      value: "",
      items: [],
      error: null,
      searchWords: [],
      page: 1,
      fetchingNew: false,
      lastPage: true,
    };
    this.state = this.initialState;
    this.timeout = null;
    this.id = cuid();
  }

  open = () => {
    const { onOpen } = this.props;
    this.setState(p => {
      if (p.value.length > 0) return { open: true };
      return null;
    });
    if (onOpen) onOpen();
  };

  close = () => {
    const { onClose } = this.props;
    this.setState({ open: false });
    if (onClose) onClose();
  };

  clear = () => {
    this.setState({ ...this.initialState });
  };

  set = value => {
    this.setState({ value });
  };

  scrollTop = () => {
    const wrapper = document.getElementById(this.id);
    if (wrapper) {
      wrapper.scrollTop = 0;
    }
  };

  loadPage = async () => {
    const page = this.state.page + 1;
    const val = this.state.value;
    if (this.state.lastPage) return;
    this.setState({ fetchingNew: true });
    const { payload, error, isCanceled } = await to(this.props.searchFunc(val, page));
    if (isCanceled) return;
    if (!error) {
      this.setState(p => {
        const merged = [...p.items, ...payload.results];
        const normalized = normalize(merged);
        return {
          items: mergeDedupe(normalized.ids).map(id => normalized.elements[id]),
          page,
          fetchingNew: false,
          lastPage: payload.next === null,
        };
      });
    } else {
      this.setState({ fetchingNew: false, lastPage: true });
    }
  };

  handleSearch = ({ target }) => {
    clearTimeout(this.timeout);
    const val = target.value;
    if (val === "") {
      this.clear();
      return;
    }
    this.setState({ open: true, fetching: true, value: val, searchWords: val.split(" "), page: 1 });
    this.timeout = setTimeout(async () => {
      const { payload, error, isCanceled } = await to(this.props.searchFunc(val, this.state.page));
      if (isCanceled) return;
      if (!error) {
        this.setState({
          fetching: false,
          items: payload.results,
          error: null,
          lastPage: payload.next === null,
        });
      } else {
        this.setState(() => ({
          fetching: false,
          fetchingNew: false,
          error: error.message || "Błąd wyszukiwania",
        }));
      }
    }, 200);
  };

  render() {
    const { fetching, value, items, error, searchWords, fetchingNew } = this.state;
    const {
      clickCallback,
      children,
      keyField,
      disabledItems,
      placeholder = "",
      className = "",
      onMouseEnter,
      onMouseLeave,
      onMouseLeaveContainer,
      autoFocus,
    } = this.props;

    return (
      <div className={className}>
        <div className="searcher-field">
          <input
            type="search"
            placeholder={placeholder}
            className="ignore-react-onclickoutside"
            value={value}
            onClick={this.open}
            onChange={this.handleSearch}
            autoFocus={autoFocus}
          />
          {fetching && <div className="loader" />}
          {this.state.open && (
            <Popover
              set={this.set}
              close={this.close}
              clear={this.clear}
              loadPage={this.loadPage}
              items={items}
              error={error}
              keyField={keyField}
              searchWords={searchWords}
              disabledItems={disabledItems}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onMouseLeaveContainer={onMouseLeaveContainer}
              clickCallback={clickCallback}
              fetchingNew={fetchingNew}
            >
              {children}
            </Popover>
          )}
        </div>
      </div>
    );
  }
}

ListSearchBox.propTypes = {
  disabledItems: PropTypes.arrayOf(PropTypes.number).isRequired,
  clickCallback: PropTypes.func.isRequired,
  searchFunc: PropTypes.func.isRequired,
  keyField: PropTypes.string.isRequired,
  onMouseLeaveContainer: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.any,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  autoFocus: PropTypes.bool,
};

const Popover = ({
  close,
  clear,
  set,
  items,
  error,
  searchWords,
  clickCallback,
  keyField,
  disabledItems,
  children,
  onMouseEnter,
  onMouseLeave,
  onMouseLeaveContainer,
  loadPage,
  fetchingNew,
}) => {
  let content;

  if (children) {
    content = children({ items });
  } else if (items.length === 0) {
    content = <div className="searcher_results-list--result">Brak wyników.</div>;
  } else if (error) {
    content = <div className="searcher_results-list--result">{error}</div>;
  } else {
    content = (
      <React.Fragment>
        {items.map(item => {
          const disabled = disabledItems.find(el => el === item.id);
          const disabledClass = disabled ? itemDisabled : "";
          const callback = disabled
            ? undefined
            : () => {
                clickCallback(item, { close, clear, set });
              };
          const onEnter = onMouseEnter ? () => onMouseEnter(item) : undefined;
          const onLeave = onMouseLeave ? () => onMouseLeave(item) : undefined;

          return (
            <div
              className={`searcher_results-list--result ${disabledClass}`}
              key={item.id}
              onClick={callback}
              onMouseEnter={onEnter}
              onMouseLeave={onLeave}
            >
              <Highlighter
                highlightClassName={css`
                  font-weight: 600;
                  font-style: normal;
                `}
                className="result_match--sugestion"
                searchWords={searchWords}
                autoEscape={true}
                textToHighlight={item[keyField]}
              />
            </div>
          );
        })}
        <Waypoint bottomOffset="-200px" onEnter={loadPage} />
        {fetchingNew && (
          <InfiniteLoader>
            <span>Wczytywanie...</span>
          </InfiniteLoader>
        )}
      </React.Fragment>
    );
  }

  return (
    <ClickOutsideListener close={close}>
      <div
        className="searcher_results-list--wrapper"
        style={{ overflowY: "auto", maxHeight: 400 }}
        onMouseLeave={onMouseLeaveContainer}
      >
        {content}
      </div>
    </ClickOutsideListener>
  );
};

Popover.propTypes = {
  disabledItems: PropTypes.arrayOf(PropTypes.number).isRequired,
  clickCallback: PropTypes.func.isRequired,
  searchWords: PropTypes.array.isRequired,
  fetchingNew: PropTypes.bool.isRequired,
  keyField: PropTypes.string.isRequired,
  loadPage: PropTypes.func.isRequired,
  items: PropTypes.array.isRequired,
  close: PropTypes.func.isRequired,
  clear: PropTypes.func.isRequired,
  set: PropTypes.func.isRequired,
  onMouseLeaveContainer: PropTypes.func,
  placeholder: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  error: PropTypes.string,
  children: PropTypes.any,
};

const itemDisabled = css`
  background-color: #efeeec;
  :hover {
    cursor: not-allowed;
  }
`;

const InfiniteLoader = styled("div")`
  padding: 10px;
  border-bottom: 1px solid #efefef;
  font-size: 16px;
`;

export default ListSearchBox;



// WEBPACK FOOTER //
// ./src/components/ListSearchBox.jsx